import React, { useState, useCallback, useMemo } from "react";
import { useRouter } from "next/navigation";
import PropTypes from "prop-types";
import Link from "next/link";

import {
  Chip,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  useMediaQuery,
  Box,
} from "@mui/material";
import { styled, useTheme, keyframes } from "@mui/material/styles";

const scaleAnimation = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(0.95); }
  75% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const spinAnimation = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const StyledListItemButton = styled(ListItemButton, {
  shouldForwardProp: (prop) =>
    !["isLoading", "isClicked", "isActive"].includes(prop),
})(({ theme, isLoading, isClicked, isActive }) => ({
  marginBottom: theme.breakpoints.up("lg") ? "25px" : "0",
  marginRight: theme.breakpoints.up("lg") ? "0" : "25px",
  padding: "5px",
  width: "50px",
  height: "50px",
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
  overflow: "visible",
  transition: theme.transitions.create(["all"], {
    duration: theme.transitions.duration.standard,
    easing: theme.transitions.easing.easeInOut,
  }),
  backgroundColor: isActive
    ? theme.palette.text.primary
    : theme.palette.background.paper,
  opacity: isLoading ? 0.8 : 1,
  animation:
    isClicked && !isLoading
      ? `${scaleAnimation} 0.4s ${theme.transitions.easing.easeInOut}`
      : "none",

  "&:hover": {
    backgroundColor: isActive
      ? theme.palette.grey[600]
      : theme.palette.grey[300],
    transform: !isLoading ? "scale(1.05)" : "none",
  },

  "&.Mui-selected": {
    color: theme.palette.primary.contrastText,
    backgroundColor: theme.palette.grey[500],
    "&:hover": {
      backgroundColor: theme.palette.grey[600],
    },
  },

  "&.Mui-disabled": {
    opacity: 0.6,
    pointerEvents: "none",
  },

  "&:before": {
    display: "none",
  },
}));

const ProgressRing = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  width: "50px",
  height: "50px",
  borderRadius: "50%",
  pointerEvents: "none",
  zIndex: 2,

  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    borderRadius: "50%",
    border: `2px solid transparent`,
    borderTopColor: theme.palette.grey[400],
    animation: `${spinAnimation} 0.8s linear infinite`,
  },
}));

const NavItem = React.memo(({ item, pathDirect, hideMenu, onClick }) => {
  const router = useRouter();
  const theme = useTheme();

  const lgDown = useMediaQuery(theme.breakpoints.down("lg"));

  const [isLoading, setIsLoading] = useState(false);
  const [isClicked, setIsClicked] = useState(false);

  const isActive = useMemo(
    () => pathDirect === item.href,
    [pathDirect, item.href]
  );

  const itemIcon = useMemo(() => {
    const Icon = item?.icon;
    return <Icon sx={item.sx || { fontSize: "1.8rem" }} />;
  }, [item.icon, item.sx]);

  const handleClick = useCallback(
    async (e) => {
      e.preventDefault();

      if (isActive || isLoading || item?.disabled) {
        return;
      }

      setIsClicked(true);

      setTimeout(() => setIsLoading(true), 50);

      try {
        const clickTimeout = setTimeout(() => setIsClicked(false), 400);

        await router.push(item.href);

        setIsLoading(false);

        if (lgDown && onClick) {
          onClick();
        }

        clearTimeout(clickTimeout);
      } catch (error) {
        console.error("Navigation error:", error);
        setIsLoading(false);
        setIsClicked(false);
      }
    },
    [isActive, isLoading, item?.disabled, item.href, router, lgDown, onClick]
  );

  return (
    <List component="li" disablePadding>
      <Box sx={{ position: "relative" }}>
        <Link href={item.href} prefetch={item.prefetch} passHref>
          <StyledListItemButton
            disabled={item?.disabled || isLoading}
            selected={isActive}
            onClick={handleClick}
            isLoading={isLoading}
            isClicked={isClicked}
            isActive={isActive}
            aria-label={item?.ariaLabel || item?.title}
          >
            {/* Progress ring around button */}
            {isLoading && <ProgressRing />}

            <ListItemIcon
              sx={{
                minWidth: 0,
                color: isActive ? "white" : "text.secondary",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
                height: "100%",
                position: "relative",
                zIndex: 1,
              }}
            >
              {itemIcon}
            </ListItemIcon>

            {!hideMenu && (
              <ListItemText
                primary={item?.title ? t(item.title) : ""}
                secondary={
                  item?.subtitle ? (
                    <Typography variant="caption" sx={{
                      color: "text.secondary"
                    }}>
                      {item.subtitle}
                    </Typography>
                  ) : null
                }
              />
            )}

            {item?.chip && !hideMenu && (
              <Chip
                color={item.chipColor || "primary"}
                variant={item.variant || "filled"}
                size="small"
                label={item.chip}
              />
            )}
          </StyledListItemButton>
        </Link>
      </Box>
    </List>
  );
});

NavItem.displayName = "NavItem";

NavItem.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    ariaLabel: PropTypes.string,
    icon: PropTypes.elementType.isRequired,
    href: PropTypes.string.isRequired,
    size: PropTypes.string,
    disabled: PropTypes.bool,
    chip: PropTypes.string,
    chipColor: PropTypes.string,
    variant: PropTypes.string,
    subtitle: PropTypes.string,
    prefetch: PropTypes.bool,
  }).isRequired,
  pathDirect: PropTypes.string.isRequired,
  hideMenu: PropTypes.bool,
  onClick: PropTypes.func,
};

NavItem.defaultProps = {
  hideMenu: false,
  onClick: null,
};

export default NavItem;
